import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ViewOnlyContext } from '@/components/templates/app-shell'
import { DataTableBlock } from '@/components/templates/list-page'
import { AddChip } from '@/components/ui/add-chip'
import { TooltipProvider } from '@/components/ui/tooltip'
import { getJSON } from '@/lib/api'
import { canManage, type StaffCapabilities } from '@/pages/staffCapabilities'
import { RosterToolbar } from './RosterToolbar'
import { initialRosterState, rosterColumns, type RosterState } from './roster-columns'
import { EnrolmentDialog } from './EnrolmentDialog'
import { EnrolmentDeletionDialog } from './EnrolmentDeletionDialog'
import type { Enrolment } from './enrolment'

type Person = { id: number; name: string }
type Learning = { enrolments: Enrolment[]; completed_credits: string }
const initialState = { ...initialRosterState, sort: 'activity' }
type Dialog = { kind: 'edit' | 'delete'; row: Enrolment } | { kind: 'add' }

export function ContactLearningPanel({
  person,
  capabilities,
}: {
  person: Person
  capabilities?: StaffCapabilities
}) {
  const viewOnly = !canManage(capabilities ?? {}, 'courses')
  return (
    <ViewOnlyContext.Provider value={viewOnly}>
      <LearningPanel key={person.id} person={person} viewOnly={viewOnly} />
    </ViewOnlyContext.Provider>
  )
}

function useLearning(contactId: number, state: RosterState) {
  const endpoint = `/api/v1/contacts/${contactId}/enrolments`
  const rows = useQuery({
    queryKey: ['enrolments', 'contact', contactId, state],
    queryFn: () => getJSON<Learning>(`${endpoint}?${new URLSearchParams({ ...state })}`),
  })
  const summary = useQuery({
    queryKey: ['enrolments', 'contact-summary', contactId],
    queryFn: () => getJSON<Learning>(endpoint),
  })
  const settings = useQuery({
    queryKey: ['credit-unit-settings'],
    queryFn: () =>
      getJSON<{ credit_unit_settings: { label: string } }>('/api/v1/credit_unit_settings'),
  })
  const courses = useQuery({
    queryKey: ['courses', 'learning-statuses'],
    queryFn: () =>
      getJSON<{ courses: { id: number; status: string }[] }>('/api/v1/courses?status=all'),
  })
  const queries = [rows, summary, settings, courses]
  const pending = queries.some((query) => query.isPending)
  const error = queries.find((query) => query.isError)?.error
  return {
    rows: rows.data?.enrolments ?? [],
    summary: summary.data,
    unit: settings.data?.credit_unit_settings?.label ?? '',
    courseStatuses: new Map(courses.data?.courses?.map((course) => [course.id, course.status])),
    tableState: error ? ('error' as const) : pending ? ('loading' as const) : ('ready' as const),
    error,
  }
}

function LearningPanel({ person, viewOnly }: { person: Person; viewOnly: boolean }) {
  const [state, setState] = useState(initialState)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [focusId, setFocusId] = useState<number | null>(null)
  const client = useQueryClient()
  const queries = useLearning(person.id, state)
  const update = (patch: Partial<RosterState>) => setState((current) => ({ ...current, ...patch }))
  const toggleSort = (sort: string) =>
    update({ sort, direction: state.sort === sort && state.direction === 'asc' ? 'desc' : 'asc' })
  const { unit, courseStatuses } = queries
  const filtered = Boolean(state.search || state.status || state.owner)
  useEffect(() => {
    if (!focusId) return
    const element = document.getElementById(`enrolment-${focusId}`)
    if (!element) return
    element.focus()
    element.scrollIntoView?.({ block: 'nearest' })
    setFocusId(null)
  }, [focusId, queries.rows])
  const onExisting = (id: number) => {
    setState(initialState)
    setDialog(null)
    setFocusId(id)
    void client.invalidateQueries({ queryKey: ['enrolments'] })
  }
  return (
    <section className="space-y-3" aria-labelledby="learning-heading">
      <div className="flex flex-wrap items-center gap-2">
        <LearningHeading summary={queries.summary} unit={unit} />
        {!viewOnly && (
          <span className="ml-auto">
            <AddChip aria-label="Add enrolment" onClick={() => setDialog({ kind: 'add' })}>
              Add enrolment
            </AddChip>
          </span>
        )}
      </div>
      <RosterToolbar
        state={state}
        update={update}
        rows={queries.summary?.enrolments ?? []}
        searchLabel="Search courses"
        exportPath={`/api/v1/contacts/${person.id}/enrolments/export`}
      />
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <TooltipProvider>
          <DataTableBlock
            table={{
              rows: queries.rows,
              columns: rosterColumns(state, toggleSort, unit, {
                viewOnly,
                courseStatuses,
                onEdit: (row) => setDialog({ kind: 'edit', row }),
                onDelete: (row) => setDialog({ kind: 'delete', row }),
              }),
              getRowId: (row) => row.id,
              getRowLabel: (row) => `Enrolment in ${row.course_title}`,
              state: queries.tableState,
              loadingTitle: 'Loading learning',
              errorTitle: 'Learning unavailable',
              errorDescription: queries.error?.message,
              emptyTitle: filtered ? 'No matching enrolments' : 'No enrolments recorded',
              emptyVariant: filtered ? 'default' : 'quiet',
            }}
          />
        </TooltipProvider>
      </div>
      {dialog && (
        <LearningDialog
          dialog={dialog}
          person={person}
          unit={unit}
          onClose={() => setDialog(null)}
          onExisting={onExisting}
        />
      )}
    </section>
  )
}

function LearningHeading({ summary, unit }: { summary?: Learning; unit: string }) {
  return (
    <h3 id="learning-heading" className="text-body font-semibold text-foreground">
      Learning{summary?.enrolments && ` · ${summary.enrolments.length}`}{' '}
      {summary?.completed_credits && unit && (
        <span className="text-caption font-normal text-muted-foreground">
          · {summary.completed_credits} {unit} completed
        </span>
      )}
    </h3>
  )
}

function LearningDialog({
  dialog,
  person,
  unit,
  onClose,
  onExisting,
}: {
  dialog: Dialog
  person: Person
  unit: string
  onClose: () => void
  onExisting: (id: number) => void
}) {
  if (dialog.kind === 'delete')
    return <EnrolmentDeletionDialog enrolment={dialog.row} onClose={onClose} />
  const row = dialog.kind === 'edit' ? dialog.row : undefined
  return (
    <EnrolmentDialog
      person={person}
      courseId={row?.course_id}
      courseTitle={row?.course_title}
      enrolment={row}
      unit={unit}
      onClose={onClose}
      onExisting={onExisting}
    />
  )
}
